import React from "react";
import { HashRouter } from "react-router-dom";
import { Layout } from "../../components/app/Layout";
import { UserProvider } from "../../context/UserContext";
import { AccountResolver } from "./AccountResolver";
import { AppOverlayController, useOverlayControl } from "./AppOverlayController";
import { AuthShell } from "./AuthShell";
import { ConnectivityMonitor } from "./ConnectivityMonitor";
import { GraphQLShell } from "./GraphQLShell";
import { ConnectivityOverlay } from "./overlays/ConnectivityOverlay";
import { SessionExpiredOverlay } from "./overlays/SessionExpiredOverlay";
import { SessionExpiryWatcher } from "./SessionExpiryWatcher";
import { ThemePreferenceLoader } from "./ThemePreferenceLoader";
import { ThemeShell } from "./ThemeShell";

// Reads overlay state and renders the appropriate full-screen UI.
// Must be inside both GraphQLShell (for Apollo) and ConnectivityMonitor (for countdown context).
const OverlayRenderer: React.FC = () => {
  const { activeOverlay } = useOverlayControl();
  if (activeOverlay === "connectivity_lost") return <ConnectivityOverlay />;
  if (activeOverlay === "session_expired") return <SessionExpiredOverlay />;
  return null;
};

// Authenticated app. On session expiry we UNMOUNT it (not just cover it) so no user data is left in
// the DOM behind the overlay; the providers above stay mounted so "Sign In" still works. Connectivity
// loss is intentionally not handled here — that overlay is transient and the app must stay mounted.
const AppContent: React.FC = () => {
  const { sessionExpired } = useOverlayControl();
  if (sessionExpired) return null;
  return (
    <UserProvider>
      <ThemePreferenceLoader />
      <Layout />
    </UserProvider>
  );
};

// Provider stack — order is intentional:
//
//   AppOverlayController   owns overlay state (connectivity_lost / session_expired)
//     AuthShell            handles Auth0 only; doesn't know about overlays
//       SessionExpiryWatcher  proactively detects session expiry on a timer; needs useAuth0() and,
//                             being a child of AuthShell, only runs once authenticated
//       AccountResolver    resolves which account the user is viewing
//         GraphQLShell     creates the Apollo client (ApolloProvider)
//           ConnectivityMonitor  heartbeat polling + refetch on recovery; needs useApolloClient()
//             OverlayRenderer    reads overlay state + countdown; needs ConnectivityMonitor context
//
// AppOverlayController sits above AuthShell so the connectivity overlay can fire even before
// auth completes. ConnectivityMonitor is inside GraphQLShell so it can call useApolloClient().
export const AppShell: React.FC = () => (
  <ThemeShell>
    <HashRouter>
      <AppOverlayController>
        <AuthShell>
          <SessionExpiryWatcher />
          <AccountResolver>
            <GraphQLShell>
              <ConnectivityMonitor>
                <OverlayRenderer />
                <AppContent />
              </ConnectivityMonitor>
            </GraphQLShell>
          </AccountResolver>
        </AuthShell>
      </AppOverlayController>
    </HashRouter>
  </ThemeShell>
);
